// services/cacheManager.ts

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes default TTL
const ERROR_TIMEOUT = 5 * 60 * 1000; // 5 minutes block after 403/500

const IDB_NAME = 'LibriTui_CacheDB';
const IDB_STORE = 'global_cache';
const IDB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;
const initIDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = (e: any) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = (e: any) => resolve(e.target.result);
    req.onerror = (e: any) => reject(e.target.error);
  });
  return dbPromise;
};

const executeIDB = async <T>(operation: (db: IDBDatabase) => Promise<T>): Promise<T> => {
  try {
    const db = await initIDB();
    return await operation(db);
  } catch (err: any) {
    if (err && (err.name === 'InvalidStateError' || err.message?.includes('closing') || err.message?.includes('closed'))) {
      dbPromise = null;
      const db = await initIDB();
      return await operation(db);
    }
    throw err;
  }
};

const idbGet = async (key: string): Promise<any> => {
  try {
    return await executeIDB((db) => {
      return new Promise((resolve, reject) => {
        try {
          const tx = db.transaction([IDB_STORE], 'readonly');
          const req = tx.objectStore(IDB_STORE).get(key);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        } catch (e) {
          reject(e);
        }
      });
    });
  } catch { return null; }
};

const idbSet = async (key: string, value: any): Promise<void> => {
  try {
    return await executeIDB((db) => {
      return new Promise((resolve, reject) => {
        try {
          const tx = db.transaction([IDB_STORE], 'readwrite');
          const req = tx.objectStore(IDB_STORE).put(value, key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        } catch (e) {
          reject(e);
        }
      });
    });
  } catch (e) { console.warn('IDB Set Error', e); }
};

const idbDelete = async (key: string): Promise<void> => {
  try {
    return await executeIDB((db) => {
      return new Promise((resolve, reject) => {
        try {
          const tx = db.transaction([IDB_STORE], 'readwrite');
          const req = tx.objectStore(IDB_STORE).delete(key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        } catch (e) {
          reject(e);
        }
      });
    });
  } catch {}
};

class CacheService {
  private memoryCache = new Map<string, CacheEntry<any>>();
  private errorBlocks = new Map<string, number>();
  private pendingRequests = new Map<string, Promise<any>>();

  /**
   * Executes a Stale-While-Revalidate fetch.
   * Checks Memory -> IndexedDB -> LocalStorage -> Network.
   */
  async swr<T>(
    key: string,
    fetcher: () => Promise<T>,
    onData: (data: T, isCached: boolean) => void,
    ttl: number = DEFAULT_TTL
  ): Promise<void> {
    const memory = this.memoryCache.get(key);
    let cachedData: T | null = null;
    let cachedTime = 0;

    if (memory) {
      cachedData = memory.data;
      cachedTime = memory.timestamp;
    } else {
      // Async lookup in IDB
      const idbData = await idbGet(key);
      if (idbData) {
        cachedData = idbData.data;
        cachedTime = idbData.timestamp;
        this.memoryCache.set(key, idbData);
      } else {
        // Fallback to local storage
        const localStr = localStorage.getItem(key);
        if (localStr) {
          try {
            const parsed: CacheEntry<T> = JSON.parse(localStr);
            cachedData = parsed.data;
            cachedTime = parsed.timestamp;
            this.memoryCache.set(key, parsed);
          } catch (e) {}
        }
      }
    }

    if (cachedData !== null && cachedData !== undefined) {
      onData(cachedData, true);
    }

    // Smart Debounce: Skip network if within TTL
    if (Date.now() - cachedTime < ttl) {
      return; 
    }

    // Error Block Check
    const blockedUntil = this.errorBlocks.get(key);
    if (blockedUntil && Date.now() < blockedUntil) {
      return; 
    }

    // Network Revalidation
    let freshData;
    if (this.pendingRequests.has(key)) {
      try {
        freshData = await this.pendingRequests.get(key);
      } catch (err) {
        return;
      }
    } else {
      const fetchPromise = fetcher();
      this.pendingRequests.set(key, fetchPromise);
      try {
        freshData = await fetchPromise;
      } finally {
        this.pendingRequests.delete(key);
      }
    }

    try {
      if (freshData !== undefined) {
        const cacheEntry = { data: freshData, timestamp: Date.now() };
        this.memoryCache.set(key, cacheEntry);
        
        // Save large data to IDB async
        idbSet(key, cacheEntry).catch(() => {});
        
        try {
          // Still try to save to localStorage for sync backward compat if small
          const json = JSON.stringify(cacheEntry);
          if (new Blob([json]).size <= 4 * 1024 * 1024) {
            localStorage.setItem(key, json);
          }
        } catch (e) {}

        onData(freshData, false);
      }
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      const msg = err?.message || '';
      const isCloudflareChallenge = msg.includes('Cloudflare') || msg.includes('Desafio');
      const isCriticalError = status === 403 || status === 500 || status === 429 || msg.includes('403') || msg.includes('429');

      if (isCriticalError) {
        if (!isCloudflareChallenge) this.errorBlocks.set(key, Date.now() + ERROR_TIMEOUT);
      }
    }
  }

  async clear(key: string) {
    this.memoryCache.delete(key);
    localStorage.removeItem(key);
    await idbDelete(key);
    this.errorBlocks.delete(key);
  }

  set(key: string, data: any) {
    const cacheEntry = { data, timestamp: Date.now() };
    this.memoryCache.set(key, cacheEntry);
    idbSet(key, cacheEntry).catch(() => {});
    try {
      const json = JSON.stringify(cacheEntry);
      if (new Blob([json]).size <= 4 * 1024 * 1024) {
        localStorage.setItem(key, json);
      }
    } catch (e) {}
  }

  setWithLimit(key: string, data: any, maxBytes: number = 10 * 1024 * 1024) {
    this.set(key, data);
  }

  get<T>(key: string): T | null {
    const memory = this.memoryCache.get(key);
    if (memory) return memory.data as T;
    const localStr = localStorage.getItem(key);
    if (localStr) {
      try {
        const parsed: CacheEntry<T> = JSON.parse(localStr);
        this.memoryCache.set(key, parsed);
        return parsed.data;
      } catch (e) {}
    }
    return null;
  }

  async getAsync<T>(key: string): Promise<T | null> {
    const mem = this.get<T>(key);
    if (mem) return mem;
    const idbData = await idbGet(key);
    if (idbData) {
      this.memoryCache.set(key, idbData);
      return idbData.data;
    }
    return null;
  }

  async clearAll() {
    this.memoryCache.clear();
    this.errorBlocks.clear();
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('data_')) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    
    // Clear IDB
    try {
      const db = await initIDB();
      const tx = db.transaction([IDB_STORE], 'readwrite');
      tx.objectStore(IDB_STORE).clear();
    } catch {}
  }
}

export const cacheManager = new CacheService();
